import React, {
	type ReactNode,
	type Ref,
	type RefObject,
	useCallback,
	useEffect,
	useMemo,
	useRef,
} from 'react';

import { loadEntryPoint } from 'react-relay';

import mergeRefs from '@atlaskit/ds-lib/merge-refs';
import { useId } from '@atlaskit/ds-lib/use-id';
import type { AnyEntryPoint, EntryPointRef } from '@atlassian/entry-point-types/types';
import { usePressablePreloadRef } from '@atlassian/entry-point-use-trigger';
import { useRelayEnvironmentProvider } from '@atlassian/relay-environment-provider';

import { usePanelActionsInternal } from '../panel-manager/panel-provider';
import { usePanelStateInternal } from '../panel-manager/use-panel-state-internal';

const emptyParams = {} as Record<string, any>;
const emptyProps = {} as Record<string, any>;
const PANEL_INSTANCE_PREFIX = 'panel-';

export type TriggerProps<TEntryPoint extends AnyEntryPoint = AnyEntryPoint> = {
	children: ({ ref }: { ref: RefObject<HTMLElement> }) => ReactNode;
	panel: TEntryPoint;
	params?: Record<string, any>;
	entryPointProps?: Record<string, any>;
	forwardedRef?: Ref<HTMLElement>;
	/**
	 * Optional instance ID for this panel. If not provided, a unique ID will be automatically generated.
	 *
	 * **Best Practice:** Let Trigger generate the ID automatically unless you need to reference
	 * the panel elsewhere (e.g., for programmatic closing). This keeps ID generation co-located
	 * with the trigger rather than managed at the manager level.
	 *
	 * @example
	 * ```tsx
	 * // Good: Let Trigger generate the ID
	 * <Trigger panel={myEntryPoint} params={{ id: '123' }}>
	 *   {({ ref }) => <button ref={ref}>Open Panel</button>}
	 * </Trigger>
	 *
	 * // Also good: Provide ID if you need to reference it elsewhere
	 * <Trigger panel={myEntryPoint} instanceId="my-panel-123" params={{ id: '123' }}>
	 *   {({ ref }) => <button ref={ref}>Open Panel</button>}
	 * </Trigger>
	 * ```
	 */
	instanceId?: string;
	/**
	 * Load entry point on focus (for keyboard navigation).
	 * @default false
	 */
	shouldLoadOnFocus?: boolean;
	/**
	 * Preload entry point on focus (for keyboard navigation).
	 * @default false
	 */
	shouldPreloadOnFocus?: boolean;
	/**
	 * Callback fired when the panel is opened.
	 * Useful for analytics tracking.
	 */
	onOpen?: () => void;
	/**
	 * Callback fired when the panel is closed.
	 * Useful for analytics tracking.
	 */
	onClose?: () => void;
	/**
	 * Whether to open the panel on click. Set this to `false` to keep preloading behaviour without opening the panel on click.
	 * @default true
	 */
	shouldOpenPanel?: boolean;
};

export function PanelTrigger<TEntryPoint extends AnyEntryPoint = AnyEntryPoint>({
	children,
	panel,
	params,
	entryPointProps,
	forwardedRef,
	instanceId: providedInstanceId,
	shouldLoadOnFocus = false,
	shouldPreloadOnFocus = false,
	onOpen,
	onClose,
	shouldOpenPanel = true,
}: TriggerProps<TEntryPoint>): React.JSX.Element {
	const manager = usePanelActionsInternal();
	const state = usePanelStateInternal();
	const environmentProvider = useRelayEnvironmentProvider();
	const generatedId = useId();
	const triggerElementRef = useRef<HTMLElement | null>(null);

	// Use a stable instance ID to avoid SSR/client mismatch
	const instanceId = useMemo(() => {
		if (providedInstanceId) {
			return providedInstanceId;
		}
		// Use a stable generated ID to avoid SSR/client mismatch
		return `${PANEL_INSTANCE_PREFIX}${generatedId}`;
	}, [providedInstanceId, generatedId]);
	const memoizedParams = useMemo(() => params ?? emptyParams, [params]);
	const memoizedProps = useMemo(() => entryPointProps ?? emptyProps, [entryPointProps]);

	// Repeated activation of the same trigger and runtime props is intentionally a no-op.
	// Programmatic manager opens always replace, including opens with the same ID.
	const isPanelAlreadyOpen =
		state.activePanel?.instanceId === instanceId && state.activePanel.params === memoizedProps;

	// Load entry point for prefetching
	const load = useCallback(
		() => loadEntryPoint<TEntryPoint>(environmentProvider, panel, memoizedParams as any),
		[environmentProvider, panel, memoizedParams],
	);

	// Handle entry point load - open panel and store the reference
	// The entry point reference is stored in panel state so it can be used when rendering.
	const onLoad = useCallback(
		({
			dispose: _dispose,
			reference,
		}: {
			dispose: () => void;
			reference: EntryPointRef<TEntryPoint>;
		}) => {
			manager.saveEntryPointReference(instanceId, reference);

			if (isPanelAlreadyOpen) {
				// the preload ref will handle cleanup when the trigger unmounts
				return;
			}

			if (!shouldOpenPanel) {
				return;
			}

			// Pass the loaded entry point reference to the manager so it can be used for rendering
			manager.openPanel({
				instanceId,
				entryPoint: panel,
				params: memoizedProps,
				entryPointReference: reference,
			});
			if (onOpen) {
				onOpen();
			}
		},
		[isPanelAlreadyOpen, manager, instanceId, panel, memoizedProps, onOpen, shouldOpenPanel],
	);

	// Monitor panel state to detect when panel closes and restore focus
	const prevPanelRef = useRef(state.activePanel);
	useEffect(() => {
		const wasOpen = prevPanelRef.current?.instanceId === instanceId;
		const isNowOpen = state.activePanel?.instanceId === instanceId;

		// Panel was open but is now closed
		if (wasOpen && !isNowOpen) {
			// Restore focus to trigger element
			if (triggerElementRef.current) {
				triggerElementRef.current.focus();
			}
			if (onClose) {
				onClose();
			}
		}

		prevPanelRef.current = state.activePanel;
	}, [state.activePanel, instanceId, onClose]);

	// Use usePressablePreloadRef for prefetching, GC, and hover-on-intent
	const triggerRef = usePressablePreloadRef<EntryPointRef<TEntryPoint>>({
		load,
		onLoad,
		loadOnFocus: shouldLoadOnFocus,
		preloadOnFocus: shouldPreloadOnFocus,
	});

	// Merge refs: usePressablePreloadRef ref + forwardedRef + triggerElementRef for focus management
	const mergedRef = useMemo(
		() =>
			mergeRefs<HTMLElement>([
				triggerRef,
				forwardedRef || null,
				(node: HTMLElement | null) => {
					triggerElementRef.current = node;
				},
			]),
		[triggerRef, forwardedRef],
	);

	return (
		<>
			{children({
				ref: mergedRef as unknown as React.RefObject<HTMLElement>,
			})}
		</>
	);
}

// Export as Trigger for public API
// eslint-disable-next-line @atlaskit/volt-strict-mode/no-multiple-exports
export const Trigger: typeof PanelTrigger = PanelTrigger;
